// src/config/api.js
// API 配置文件

// 后端基础URL
// 浏览器开发环境走 vue.config.js 中的 devServer 代理，避免 CORS
// Tauri 桌面端（含 tauri dev）必须直连完整 URL，相对路径会命中本地页面 HTML
const PROD_API_BASE_URL = 'https://mmradar.inchitech.com'
// export const PROD_API_BASE_URL = 'http://172.16.30.12:5150'

export { PROD_API_BASE_URL }

function shouldUseDevProxy() {
  if (process.env.NODE_ENV !== 'development') return false
  if (typeof window === 'undefined') return true
  return !(
    window.__TAURI_INTERNALS__ ||
    window.__TAURI__ ||
    window.location.hostname === 'tauri.localhost'
  )
}

export const API_BASE_URL = shouldUseDevProxy() ? '' : PROD_API_BASE_URL

// 认证相关API接口
export const AUTH_API = {
  /**
   * 密码登录接口
   * 
   * 请求参数:
   * {
   *   "phone": "string",        // 用户名或手机号
   *   "password": "string",        // 加密后的密码
   * }
   * 
   * 响应数据:
   * {
   *   "code": "number",            // 状态码 200表示成功
   *   "message": "string",         // 响应消息
   *   "data": {
   *     "sessionId": "string",     // 会话ID
   *   }
   * }
   */
  LOGIN_PASSWORD: `${API_BASE_URL}/api/manage/user/loginWeb`,

  /** 获取 token（24 小时有效，文档 2.2） */
  GET_VIDEO_TOKEN: `${API_BASE_URL}/api/manage/user/getVideoToken`,

  /** 验证 token 是否有效（文档 2.3） */
  VALIDATE_TOKEN: `${API_BASE_URL}/api/manage/user/validateToken`,

  /** 刷新 session，延长 24 小时有效期 */
  REFRESH_SESSION: `${API_BASE_URL}/api/manage/user/refreshSession`,

  /**
   * 短信验证码登录接口
   * 
   * 请求参数:
   * {
   *   "phone": "string",           // 手机号
   *   "code": "string"             // 短信验证码
   * }
   * 
   * 响应数据:
   * (同密码登录接口一致)
   */
  LOGIN_SMS: `${API_BASE_URL}/api/manage/user/app/loginSMS`,

  /**
   * 邮箱登录接口
   * 
   * 请求参数:
   * {
   *   "email": "string",           // 邮箱地址
   *   "code": "string"             // 邮箱验证码
   * }
   * 
   * 响应数据:
   * {
   *   "success": "boolean",        // 是否成功
   *   "message": "string",         // 响应消息
   *   "accessToken": "string",     // 访问令牌
   *   "refreshToken": "string",    // 刷新令牌
   *   "expiresIn": "number",       // 过期时间(秒)
   *   "user": {                    // 用户信息
   *     "id": "string",            // 用户ID
   *     "username": "string",      // 用户名
   *     "email": "string",         // 邮箱
   *     "roles": "array"           // 用户角色
   *   }
   * }
   */
  LOGIN_EMAIL: `${API_BASE_URL}/api/auth/login/email`,

  /**
   * 用户注册接口
   * 
   * 请求参数:
   * {
   *   "phone": "string",           // 手机号
   *   "code": "string",            // 短信验证码
   *   "password": "string"         // 密码
   * }
   * 
   * 响应数据:
   * {
   *   "success": "boolean",        // 是否成功
   *   "message": "string",         // 响应消息
   *   "userId": "string"           // 用户ID
   * }
   */
  REGISTER: `${API_BASE_URL}/api/manage/user/register`,

  /**
   * 发送短信验证码接口
   * 
   * 请求参数:
   * {
   *   "phone": "string"            // 手机号
   * }
   * 
   * 响应数据:
   * {
   *   "code": "string",            // 状态码 200表示成功
   *   "message": "string"          // 响应消息
   * }
   */
  SEND_SMS_CODE: `${API_BASE_URL}/api/manage/user/app/sendCode`,

  /**
   * 发送邮箱验证码接口
   * 
   * 请求参数:
   * {
   *   "email": "string"            // 邮箱地址
   * }
   * 
   * 响应数据:
   * {
   *   "success": "boolean",        // 是否成功
   *   "message": "string"          // 响应消息
   * }
   */
  SEND_EMAIL_CODE: `${API_BASE_URL}/api/manage/user/app/sendEmailCode`,

  /**
   * MFA验证接口
   * 
   * 请求参数:
   * {
   *   "code": "string"             // MFA验证码
   * }
   * 
   * 响应数据:
   * {
   *   "success": "boolean",        // 是否成功
   *   "message": "string",         // 响应消息
   *   "accessToken": "string",     // 访问令牌
   *   "refreshToken": "string",    // 刷新令牌
   *   "expiresIn": "number",       // 过期时间(秒)
   *   "user": {                    // 用户信息
   *     "id": "string",            // 用户ID
   *     "username": "string",      // 用户名
   *     "email": "string",         // 邮箱
   *     "roles": "array"           // 用户角色
   *   }
   */
  VERIFY_MFA: `${API_BASE_URL}/api/auth/verify-mfa`
};

// 家庭管理相关API接口
export const FAMILY_API = {
  /**
   * 查询家庭列表接口
   * 
   * 请求参数:
   * {
   *   "user_id": "string"          // 用户ID
   * }
   * 
   * 响应数据:
   * {
   *   "code": "number",            // 状态码 200表示成功
   *   "message": "string",         // 响应消息
   *   "data": [                    // 家庭列表
   *     {
   *       "id": "string",          // 家庭ID
   *       "name": "string"         // 家庭名称
   *     }
   *   ]
   * }
   */
  QUERY_FAMILY_LIST: `${API_BASE_URL}/api/manage/family/queryfamilylist`,

  /**
   * 创建家庭接口
   * 
   * 请求参数:
   * {
   *   "user_id": "string",         // 用户ID
   *   "family_name": "string"      // 家庭名称
   * }
   * 
   * 响应数据:
   * {
   *   "code": "number",            // 状态码 200表示成功
   *   "message": "string",         // 响应消息
   *   "data": {
   *     "id": "string",            // 家庭ID
   *     "name": "string"           // 家庭名称
   *   }
   * }
   */
  CREATE_FAMILY: `${API_BASE_URL}/api/manage/family/addfamily`,


  /**
   * 查询家庭信息接口
   *
   * 请求参数:
   *  {
   *      "user_id": "string"       // 用户ID
   *  }
   *  响应数据:
   *   {
   *       "code": "number",          // 状态码 200表示成功
   *       "message": "string",       // 响应消息
   *       "data": [
   *            {
   *                "familyId": "string",       // 家庭ID
   *                "familyName": "string",      // 家庭名称
   *                "privileges": "number",      // 权限等级
   *                "sceneCount": "number",       // 场景数量
   *                "deviceCount": "number"       // 设备数量
   *            }
   *       ]
   *   }
   */
  QUERY_FAMILY: `${API_BASE_URL}/api/manage/family/queryFamily`,
  /**
   * 查询分享家庭列表接口
   *
   * 请求参数:
   *  {
   *      "user_id": "number",       // 用户ID
   *      "family_id": "number"       // 家庭ID
   *  }
   *
   *  响应数据:
   *   {
   *       "code": "number",          // 状态码 200表示成功
   *       "message": "string",       // 响应消息
   *       "data": []
   *   }
   */
  QUERY_SHARE_FAMILY: `${API_BASE_URL}/api/manage/family/queryShareFamily`,

  /**
   * 更新家庭名称接口
   *
   * 请求参数:
   *  {
   *      "user_id": "string",       // 用户ID
   *      "family_id": "string",     // 家庭ID
   *      "family_name": "string"    // 新的家庭名称
   *  }
   *
   *  响应数据:
   *   {
   *       "code": "number",          // 状态码 200表示成功
   *       "message": "string",       // 响应消息
   *       "data": null
   *   }
   */
  UPDATE_FAMILY_NAME: `${API_BASE_URL}/api/manage/family/updatefamilyname`,

  /**
   * 删除家庭接口
   *
   * 请求参数:
   *  {
   *      "user_id": "string",       // 用户ID
   *      "family_id": "string"      // 家庭ID
   *  }
   *
   *  响应数据:
   *   {
   *       "code": "number",          // 状态码 200表示成功
   *       "message": "string",       // 响应消息
   *       "data": null
   *   }
   */
  DELETE_FAMILY: `${API_BASE_URL}/api/manage/family/deletefamily`,

  /**
   * 共享家庭接口
   *
   * 请求参数:
   *  {
   *      "user_id": "string",       // 用户ID
   *      "family_id": "string"      // 家庭ID
   *  }
   *
   *  响应数据:
   *   {
   *       "code": "number",          // 状态码 200表示成功
   *       "message": "string",       // 响应消息
   *       "data": null
   *   }
   */
  SHARE_FAMILY: `${API_BASE_URL}/api/manage/family/sharefamily`,

  /**
   * 生成分享码接口
   *
   * 请求参数:
   *  {
   *      "family_id": "string"       // 家庭ID
   *  }
   *
   *  响应数据:
   *   {
   *       "code": "number",          // 状态码 200表示成功
   *       "message": "string",       // 响应消息
   *       "data": {
   *           "code": "string"        // 分享码
   *       }
   *   }
   */
  GENERATE_SHARE_CODE: `${API_BASE_URL}/api/manage/family/generateShareCode`,

  /**
   * 加入家庭接口（通过分享码）
   *
   * 请求参数:
   *  {
   *      "share_code": "string",     // 分享码
   *      "user_id": "string"         // 用户ID
   *  }
   *
   *  响应数据:
   *   {
   *       "code": "number",          // 状态码 200表示成功
   *       "message": "string",       // 响应消息
   *       "data": null
   *   }
   */
  JOIN_FAMILY: `${API_BASE_URL}/api/manage/family/joinFamily`,
};

// 场景管理相关API接口
export const SCENE_API = {
  /**
   * 创建场景接口
   * 
   * 请求参数:
   * {
   *   "name": "string",            // 房间名称
   *   "room_x_length": "number",   // 房间长度(m)
   *   "room_y_length": "number",   // 房间宽度(m)
   *   "emergencyContact": "string", // 紧急联系人
   *   "userId": "string",          // 用户ID
   *   "family_id": "string"        // 家庭ID
   * }
   * 
   * 响应数据:
   * {
   *   "code": "number",            // 状态码 200表示成功
   *   "message": "string",         // 响应消息
   *   "data": null
   * }
   */
  CREATE_SCENE: `${API_BASE_URL}/api/manage/scene/createSceneByFamilyId`,

  /**
   * 查询场景列表接口
   * 
   * 请求参数:
   * {
   *   "family_id": "string"        // 家庭ID
   * }
   * 
   * 响应数据:
   * {
   *   "code": "number",            // 状态码 200表示成功
   *   "message": "string",         // 响应消息
   *   "data": [                    // 场景列表
   *     {
   *       "id": "number",
   *       "name": "string",
   *       "room_x_length": "string",
   *       "room_y_length": "string"
   *     }
   *   ]
   * }
   */
  QUERY_SCENE_LIST: `${API_BASE_URL}/api/manage/scene/querySceneListByFamilyId`,

  /**
   * 查询场景详情接口
   * 
   * 请求参数:
   * {
   *   "id": "number"               // 场景ID
   * }
   * 
   * 响应数据:
   * {
   *   "code": "number",            // 状态码 200表示成功
   *   "message": "string",         // 响应消息
   *   "data": {                    // 场景详情
   *     // ...
   *   }
   * }
   */
  SCENE_DETAIL: `${API_BASE_URL}/api/manage/scene/detail`,

  /**
   * 更新场景信息接口
   * 
   * 请求参数:
   * {
   *   "id": "number",              // 场景ID
   *   "userId": "number",          // 用户ID
   *   "name": "string",            // 房间名称
   *   "room_x_length": "number",   // 房间长度
   *   "room_y_length": "number",   // 房间宽度
   *   "emergencyContact": "string" // 紧急联系人
   * }
   * 
   * 响应数据:
   * {
   *   "code": "number",            // 状态码 200表示成功
   *   "message": "string",         // 响应消息
   *   "data": null
   * }
   */
  UPDATE_SCENE: `${API_BASE_URL}/api/manage/scene/update`
};

// 设备管理相关API接口
export const DEVICE_API = {
  /**
   * 获取设备列表接口
   * 
   * 请求参数:
   * {
   *   "userId": "number",          // 用户ID
   *   "sceneId": "number"          // 场景ID
   * }
   * 
   * 响应数据:
   * {
   *   "code": "number",            // 状态码 200表示成功
   *   "message": "string",         // 响应消息
   *   "data": []                   // 设备列表
   * }
   */
  LIST: `${API_BASE_URL}/api/manage/userDevice/list`,

  /**
   * 添加设备接口（微信小程序/网页版）
   * 
   * 请求参数:
   * {
   *   "roomWidth": "string/number",        // 房间宽度
   *   "roomHeight": "string/number",       // 房间高度/长度
   *   "sceneId": "string/number",          // 场景/房间ID
   *   "name": "string",                    // 设备名称
   *   "mac": "string",                     // 设备序列号/MAC地址 (15或20位数字)
   *   "radarHeight": "string/number",      // 雷达高度
   *   "downAngle": "string/number",        // 下倾角度 (默认'30')
   *   "is_sendMessage": "number",          // 是否短信推送 (0或1)
   *   "wave_trigger": "number",            // 是否挥手检测 (0或1)
   *   "fall_trigger": "number",            // 是否跌倒检测 (0或1)
   *   "LongLayDetect": "number",           // 是否滞留检测 (0或1)
   *   "SelfCalib": "number",               // 是否自校准 (0或1)
   *   "language": "string"                 // 语言设置 ('zh'中文 或 'en'英文)
   * }
   * 
   * 响应数据:
   * {
   *   "code": "number",                    // 状态码 200表示成功
   *   "message": "string",                 // 响应消息
   *   "data": "string"                     // 设备ID
   * }
   */
  ADD_DEVICE: `${API_BASE_URL}/api/manage/device/wechat/add`,

  /**
   * 更新设备位置接口
   * 
   * 请求参数:
   * [
   *   {
   *     "deviceId": "string",      // 设备ID
   *     "x_location": "number",    // X轴坐标
   *     "y_location": "number",    // Y轴坐标
   *     "azi_angle": "number"      // 角度
   *   }
   * ]
   * 
   * 响应数据:
   * {
   *   "code": "number",            // 状态码 200表示成功
   *   "message": "string",         // 响应消息
   *   "data": null
   * }
   */
  UPDATE_LOCATION: `${API_BASE_URL}/api/manage/device/updateLocation`,
  /**
   * 删除设备接口
   * 
   * 请求参数:
   * {
   *   "userId": "number",          // 用户ID
   *   "deviceId": "string",        // 设备ID/MAC
   *   "sceneId": "number"          // 场景ID
   * }
   * 
   * 响应数据:
   * {
   *   "code": "number",            // 状态码 200表示成功
   *   "message": "string",         // 响应消息
   *   "data": null
   * }
   */
  DELETE_DEVICE: `${API_BASE_URL}/api/manage/scene/delDeivce`,

  /**
   * 查询设备详情接口（含最新进度、配置）
   *
   * 请求参数:
   * {
   *   "id": "string"              // 设备ID或MAC
   * }
   */
  DEVICE_DETAIL: `${API_BASE_URL}/api/manage/device/detail`,

  /**
   * 更新设备信息接口
   *
   * 请求参数:
   * {
   *   "id": "string",             // 设备ID
   *   "mac": "string",            // 设备序列号/MAC
   *   "name": "string",           // 设备名称
   *   "radarHeight": "string",    // 雷达高度
   *   "downAngle": "string",      // 下倾角度
   *   "is_sendMessage": "number", // 短信推送 0/1
   *   "wave_trigger": "number",   // 挥手语音 0/1
   *   "fall_trigger": "number",   // 跌倒语音 0/1
   *   "LongLayDetect": "number",  // 趟地检测 0/1
   *   "SelfCalib": "number",      // 自校准 0/1
   *   "language": "string"        // 语言 zh/en
   * }
   */
  UPDATE_DEVICE: `${API_BASE_URL}/api/manage/device/updateDevice`,

  /**
   * 查询最新固件版本接口
   */
  UPLOAD_VERSION: `${API_BASE_URL}/api/manage/version/query`,

  /**
   * 设置雷达升级进度（开始/结束时置零）
   */
  SET_RADAR_PRECENT: `${API_BASE_URL}/api/manage/device/setRadarPrecent`,

  /**
   * 触发固件升级接口
   */
  POSE_UPDATE: `${API_BASE_URL}/api/manage/device/poseUpdate`
};

// 模型/家具管理相关API接口
export const MODEL_API = {
  /**
   * 获取家具列表接口
   * 
   * 请求参数:
   * {
   *   "sceneId": "number"          // 场景ID
   * }
   * 
   * 响应数据:
   * {
   *   "code": "number",            // 状态码 200表示成功
   *   "message": "string",         // 响应消息
   *   "data": []                   // 家具列表
   * }
   * 
   * 调用的API是/api/manage/model/calculate
   */
  GET_MODEL: `${API_BASE_URL}/api/manage/model/getModel`,

  /**
   * 计算房间布局接口
   * 
   * 请求参数:
   * {
   *   "sceneId": "string/number",
   *   "length": "string",
   *   "width": "string",
   *   "graphArray": [
   *     { "azi": "up", "graph": "base64..." },
   *     { "azi": "down", "graph": "base64..." },
   *     { "azi": "left", "graph": "base64..." },
   *     { "azi": "right", "graph": "base64..." }
   *   ]
   * }
   */
  CALCULATE_MODEL: `${API_BASE_URL}/api/manage/model/calculate`
};

// 用户管理相关API接口
export const USER_API = {
  /**
   * 更新用户名接口
   * 
   * 请求参数:
   * {
   *   "id": "string",           // 用户ID
   *   "name": "string"          // 新用户名
   * }
   * 
   * 响应数据:
   * {
   *   "code": "number",         // 状态码 200表示成功
   *   "message": "string",      // 响应消息
   *   "data": null               // 数据
   * }
   */
  EDIT_USER: `${API_BASE_URL}/api/manage/user/edit`,

  /**
   * 修改密码接口
   * 
   * 请求参数:
   * {
   *   "oldPassword": "string",  // 旧密码(加密后)
   *   "newPassword": "string"   // 新密码(加密后)
   * }
   * 
   * 响应数据:
   * {
   *   "code": "number",         // 状态码 200表示成功
   *   "message": "string"       // 响应消息
   * }
   */
  UPDATE_PASSWORD: `${API_BASE_URL}/api/manage/user/updatePassword`
};

// 历史回放相关API接口
export const HISTORY_API = {
  /**
   * 查询历史时间段接口
   * /api/manage/device/historyTime 采用post调用
   */
  QUERY_HISTORY_TIME: `${API_BASE_URL}/api/manage/device/historyTime`,

  /**
   * 查询历史记录详情/检查接口
   * /api/queryHistory 用post调用
   */
  QUERY_HISTORY: `${API_BASE_URL}/api/queryHistory`
};

// 告警相关API接口
// 呼吸心跳（体征）设备管理 API，数据表 device_info_breath_heart
export const BREATHE_HEART_API = {
  LIST: `${API_BASE_URL}/api/breathe`,
  CREATE: `${API_BASE_URL}/api/breathe`,
  UPDATE: (id) => `${API_BASE_URL}/api/breathe/${id}`,
  DELETE: (id) => `${API_BASE_URL}/api/breathe/${id}`,
  GET: (id) => `${API_BASE_URL}/api/breathe/${id}`,
}

// 跌倒检测设备管理 API（与 /api/breathe 分页格式一致）
export const TOPFALL_API = {
  LIST: `${API_BASE_URL}/api/topfall`,
  CREATE: `${API_BASE_URL}/api/topfall`,
  UPDATE: (id) => `${API_BASE_URL}/api/topfall/${id}`,
  DELETE: (id) => `${API_BASE_URL}/api/topfall/${id}`,
  GET: (id) => `${API_BASE_URL}/api/topfall/${id}`,
}

export const ALERT_API = {
  /**
   * 告警列表接口
   * 按设备ID/MAC 查询告警列表
   * 请求参数:
   * {
   *   "id": "string" // 设备ID/MAC
   * }
   */
  GET_ALERT_LIST: `${API_BASE_URL}/api/manage/device/queryAlertListByDeviceId`
};

export default {
  API_BASE_URL,
  AUTH_API,
  FAMILY_API,
  SCENE_API,
  USER_API,
  DEVICE_API,
  BREATHE_HEART_API,
  TOPFALL_API,
  MODEL_API,
  HISTORY_API,
  ALERT_API
};